/**
 * AccessBoardConnected — the "My Access" board wired to the live access surface,
 * for the Home detail pane and the apps/access satellite. Wrapper-as-adapter
 * (roster/allocation precedent): it fetches the frozen wire endpoints and MAPS
 * them into the presentational AccessBoard's view-model; the board is never
 * restyled.
 *
 * Data:
 *   GET /me/access/overview        → identity (org chain, leads, roles, counts)
 *   GET /me/access/resources?type= → the reachable set per type (all pages)
 *   GET /access-requests           → my outgoing requests
 *   GET /me/access/resources/{ref} → the "Why?" chain per item
 *
 * ACL is read-only here; there is no cancel-request endpoint on the frozen wire,
 * so the board's cancel affordance is intentionally not offered (never invented).
 *
 * The chat side-channel (BoardAgentChat transcript + ChatInput) mirrors
 * AllocationBoardConnected and works session-less too: its first send mints
 * the session via useDeferredFirstSend (agentSlug + facePatientId), then
 * `onSessionCreated` lets the caller transition into the real conversation.
 * The transcript itself (BoardAgentChat) stays session-gated — there is
 * nothing to fold before a session exists.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/stores/auth';
import { useStreamingMessage } from '@/hooks/useStreamingChat';
import { useDeferredFirstSend } from '@/hooks/useDeferredFirstSend';
import { sessionKeys } from '@/hooks/useSessions';
import { BoardAgentChat, ChatInput } from '@/components/chat';
import { Skeleton } from '@/components/ui/skeleton';
import {
  accessKeys,
  useAccessOverview,
  useAccessResourcesByType,
  useMyAccessRequests,
} from '@/hooks/useAccess';
import { getAccessExplanation, type AccessRef } from '@/api/endpoints/access';
import type { AccessRequest as AccessRequestWire } from '@/api/endpoints/accessRequests';
import type { SessionResponse } from '@/api/types_session';
import type { SessionDetail } from '@/hooks/useSessions';
import { AGENT_SLUG } from '../../agent';

import { AccessBoard } from './AccessBoard';
import type {
  AccessOverview,
  AccessRequest,
  AccessResourceItem,
  AccessSource,
  ExplanationStep,
  OrgNode,
} from '@frontend/components/access/types';

interface AccessBoardConnectedProps {
  /** Present on the Session page / detail pane; absent on the session-less
   *  Home face (board-only + a session-less composer — its first send mints
   *  the session, see useDeferredFirstSend). */
  session?: SessionDetail;
  /** Face mode only. My Access has no patient frame (personal scope), so this
   *  is always undefined in practice — present for AgentDetailViewProps parity. */
  facePatientId?: string | null;
  /** Face mode only — fires once the composer's first send mints the session. */
  onSessionCreated?: (session: SessionResponse) => void;
}

const refToResource = (ref: AccessRef) => ({
  type: ref.type,
  id: ref.id,
  label: ref.label && ref.label.trim() !== '' ? ref.label : ref.id,
});

/** Map the wire request row → the board's AccessRequest view-model. */
function mapRequest(r: AccessRequestWire): AccessRequest {
  return {
    id: r.id,
    requester: { id: r.requested_by, name: r.requester_name ?? '' },
    resource: {
      type: r.resource_type,
      id: r.resource_id,
      label: r.resource_title && r.resource_title.trim() !== '' ? r.resource_title : r.resource_id,
    },
    relation: r.access_level,
    justification: r.justification,
    status: r.status,
    created_at: r.created_at,
    decided_at: r.decided_at,
    decided_by: r.decided_by ? { id: r.decided_by, name: '' } : undefined,
  };
}

export function AccessBoardConnected({
  session,
  facePatientId,
  onSessionCreated,
}: AccessBoardConnectedProps) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const overview = useAccessOverview();
  const types = useMemo(
    () => Object.keys(overview.data?.counts ?? {}),
    [overview.data?.counts],
  );
  const resourceQueries = useAccessResourcesByType(types);
  const myRequests = useMyAccessRequests();

  // Flatten the per-type reachable sets into wire items (type-tagged already).
  // A FAILED type's `.data ?? []` still contributes [] here — that used to be
  // the whole bug (an error read as "zero reachable resources"). It stays []
  // for the flatten (there is genuinely nothing to render for that type), but
  // resourceLoadErrors below carries the failure forward so the board shows
  // it instead of silently under-counting.
  const wireItems = useMemo(
    () => resourceQueries.flatMap((q) => q.data ?? []),
    [resourceQueries],
  );
  const resourceLoadErrors = useMemo(
    () => types.filter((_, i) => resourceQueries[i]?.isError),
    [types, resourceQueries],
  );
  const retryResourceType = useCallback(
    (type: string) => {
      const i = types.indexOf(type);
      void resourceQueries[i]?.refetch();
    },
    [types, resourceQueries],
  );

  // "Why?" chains fetch LAZILY, one derivation per opened item — the plan's
  // cheap-overview / on-tap-explanation split (each explanation is a ListGrants
  // + structural Checks, so eagerly resolving the whole page would hammer the
  // graph). AccessBoard's `onExpandWhy` marks an item expanded on first open;
  // only expanded items' queries are enabled.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const onExpandWhy = useCallback((item: AccessResourceItem) => {
    const key = `${item.resource.type}:${item.resource.id}`;
    setExpanded((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);
  const explanationQueries = useQueries({
    queries: wireItems.map((it) => {
      const resource = `${it.resource.type}:${it.resource.id}`;
      return {
        queryKey: accessKeys.explanation(resource),
        queryFn: () => getAccessExplanation(resource),
        enabled: expanded.has(resource),
        staleTime: 5 * 60_000,
        retry: 1,
        refetchOnWindowFocus: false as const,
      };
    }),
  });
  // Retry re-marks the item expanded (a no-op if already in the set) AND
  // explicitly refetches — `onExpandWhy` alone would no-op on a query that
  // already settled (successfully OR with an error), so a failed fetch's
  // retry button needs the explicit refetch to actually re-run.
  const retryWhy = useCallback(
    (item: AccessResourceItem) => {
      const resource = `${item.resource.type}:${item.resource.id}`;
      onExpandWhy(item);
      const i = wireItems.findIndex((it) => `${it.resource.type}:${it.resource.id}` === resource);
      void explanationQueries[i]?.refetch();
    },
    [wireItems, explanationQueries, onExpandWhy],
  );

  const overviewVM: AccessOverview | null = useMemo(() => {
    if (!overview.data || !me) return null;

    const org_chain: OrgNode[] = [...overview.data.placement]
      .reverse() // wire is nearest-unit-first; the chain renders root → leaf
      .map((ref) => ({ type: ref.type, id: ref.id, label: ref.label ?? ref.id }));

    const items: AccessResourceItem[] = wireItems.map((it, i) => {
      const explanationData = explanationQueries[i]?.data;
      const explanation: ExplanationStep[] | undefined = explanationData
        ? mapExplanation(explanationData.steps, refToResource(it.resource), it.effective_role)
        : undefined;
      return {
        resource: refToResource(it.resource),
        effective_role: it.effective_role,
        source: it.source as AccessSource,
        expires_at: it.expires_at,
        explanation,
        explanationError: explanationQueries[i]?.isError ?? false,
      };
    });

    return {
      subject: {
        id: me.id,
        name: me.name ?? me.email,
        role: me.role ?? undefined,
        specialisation: me.specialisation ?? undefined,
      },
      org_chain,
      leads: overview.data.leads.map((l) => ({
        user: { id: l.principal_id, name: l.name ?? l.principal_id },
        relation: l.principal_type,
      })),
      roles: overview.data.roles.map((r) => r.name || r.group_id),
      counts: overview.data.counts,
      items,
      // A FAILED live myRequests query still falls back to the overview's
      // embedded snapshot (real data, just possibly stale) — outgoingRequestsStale
      // below is what keeps that fallback from looking indefinitely current.
      outgoing_requests: (myRequests.data ?? overview.data.outgoing_requests).map(mapRequest),
    };
  }, [overview.data, me, wireItems, explanationQueries, myRequests.data]);

  // --- Chat side-channel (works session-less too via useDeferredFirstSend) ---
  const streaming = useStreamingMessage(session?.session.id ?? '');
  const deferred = useDeferredFirstSend({
    sessionId: session?.session.id,
    agentSlug: AGENT_SLUG,
    patientId: facePatientId ?? undefined,
    sendMessage: streaming.sendMessage,
    onCreated: onSessionCreated,
  });
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (streaming.isStreaming) {
      wasStreaming.current = true;
    } else if (wasStreaming.current) {
      wasStreaming.current = false;
      void queryClient.invalidateQueries({ queryKey: accessKeys.all });
      if (session?.session.id) {
        void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(session.session.id) });
      }
    }
  }, [streaming.isStreaming, session?.session.id, queryClient]);

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-auto">
        {overviewVM ? (
          <AccessBoard
            overview={overviewVM}
            onExpandWhy={onExpandWhy}
            onRetryWhy={retryWhy}
            resourceLoadErrors={resourceLoadErrors}
            onRetryResourceType={retryResourceType}
            outgoingRequestsStale={myRequests.isError}
          />
        ) : overview.isLoading ? (
          <div className="mx-auto w-full max-w-3xl space-y-3 p-4" aria-busy="true">
            <Skeleton className="h-28 w-full rounded-[18px]" />
            <Skeleton className="h-20 w-full rounded-[18px]" />
            <Skeleton className="h-20 w-full rounded-[18px]" />
          </div>
        ) : overview.isError ? (
          <div className="p-4">
            <p className="text-[15px] text-neutral-700" role="alert">
              {t('access.board.loadError', {
                defaultValue: 'Could not load your access overview.',
              })}{' '}
              <button
                type="button"
                onClick={() => void overview.refetch()}
                className="font-semibold text-primary underline-offset-2 hover:underline"
              >
                {t('actions.retry', 'Retry')}
              </button>
            </p>
          </div>
        ) : null}
      </div>

      {session && (
        <BoardAgentChat
          session={session}
          streaming={streaming}
          className="max-h-[35%] shrink-0 overflow-y-auto border-t border-neutral-200 bg-neutral-50 px-4 py-2"
        />
      )}

      {(session || onSessionCreated) && (
        <div className="shrink-0 border-t border-neutral-200 bg-neutral-50 px-4 py-3 pb-safe">
          <ChatInput
            onSend={(content) => void deferred.send(content)}
            isPending={streaming.isStreaming || deferred.isPending}
            placeholder={t('access.board.chatPlaceholder', {
              defaultValue: 'Ask about your access…',
            })}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Map the wire derivation steps → the board's ExplanationStep ladder. The wire
 * emits intermediary hops (a via-bearing step per group/parent) plus direct/
 * public/admin edges (no via). The view-model renders a ladder whose LAST node
 * is the resource, so: each via-bearing hop becomes a node; a terminal node for
 * the resource itself is appended (carrying the direct/public/admin edge when
 * that was the reaching path). Every node is a real wire fact — nothing invented.
 */
function mapExplanation(
  steps: { kind: string; via?: AccessRef; relation: string; expires_at?: string }[],
  resource: { type: string; id: string; label: string },
  effectiveRole: string,
): ExplanationStep[] {
  const hops: ExplanationStep[] = [];
  let terminalKind = 'direct';
  let terminalRelation = effectiveRole;
  let terminalExpiresAt: string | undefined;

  for (const s of steps) {
    if (s.via) {
      hops.push({
        kind: s.kind as AccessSource,
        via: { type: s.via.type, id: s.via.id, label: s.via.label ?? s.via.id },
        relation: s.relation,
        expires_at: s.expires_at,
      });
    } else {
      // direct / public / admin: the edge lands straight on the resource.
      terminalKind = s.kind;
      terminalRelation = s.relation;
      terminalExpiresAt = s.expires_at;
    }
  }

  const last = hops[hops.length - 1];
  if (!last || last.via.id !== resource.id) {
    hops.push({
      kind: (last?.kind ?? terminalKind) as AccessSource,
      via: resource,
      relation: terminalRelation,
      expires_at: terminalExpiresAt,
    });
  }
  return hops;
}
